/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosImageFragment } from "@cosmos/hygraph-schema";
import { ArrowRightCircleFilledIcon } from "@eds-web/react/icons";

import { CosmosCard, CosmosImage, CosmosText } from "@components/elements";
import { AnalyticsProps } from "@components/types";
import { css, cx } from "@panda/css";

export type FeatureCardProps = AnalyticsProps & {
	label?: string | null;
	href?: string | null;
	img?: Omit<CosmosImageFragment, "id"> | null;
	imageLabel?: string | null;
};

const featureCard = css({
	"&::after": {
		background: "linear-gradient(0deg, rgba(16, 16, 20, 0.90), rgba(16, 16, 20, 0.00))",
		content: "''",
		height: "100%",
		insetBlockStart: 0,
		insetInline: 0,
		pointerEvents: "none",
		position: "absolute",
		width: "100%",
	},
});

const featureCardThumbnail = css({ display: "inline-block" });

const featureCardOverlay = css({
	alignItems: "end",
	color: "eds.text.contrast",
	display: "flex",
	padding: "eds.dimension.24",
});

const featureCardLink = css({
	width: "100%",
});

const featureCardText = css({
	display: "inline",
});

const featureCardIcon = css({
	display: "inline",
	transform: "translateY(16%)" /* magic number to vertically center the icon */,
	verticalAlign: "baseline",
});

export function FeatureCard({ analytics, label, href, img, imageLabel }: FeatureCardProps): React.JSX.Element {
	const words = label?.split(" ") || [];
	const lastWord = words.pop();
	if (words.length) {
		words.push("");
	}

	return (
		<CosmosCard className={cx("feature-card", featureCard)} borderRadius={16} padding={0}>
			<CosmosCard.Thumbnail aspectRatio="16/9" className={featureCardThumbnail}>
				{img ? (
					<CosmosImage
						className={css({ pointerEvents: "none" })}
						alt={imageLabel || ""}
						loading="lazy"
						height={332}
						width={590}
						src={img?.url}
					/>
				) : (
					""
				)}
				{label ? (
					<CosmosCard.ThumbnailOverlay className={featureCardOverlay}>
						<CosmosCard.Link analytics={analytics} className={featureCardLink} href={href || ""}>
							{words.length > 0 ? (
								<CosmosText as="p" className={featureCardText} variant="headingMD">
									{words.join(" ")}
									<span
										className={css({
											whiteSpace: "nowrap",
											display: "inline-flex",
											alignItems: "baseline",
											flexWrap: "nowrap",
										})}
									>
										{lastWord}{" "}
										<ArrowRightCircleFilledIcon className={cx(featureCardIcon, css({ marginInlineStart: "1" }))} />
									</span>
								</CosmosText>
							) : (
								<>
									<CosmosText
										as="p"
										className={cx(featureCardText, css({ overflowWrap: "break-word", paddingInlineEnd: "28px" }))}
										variant="headingMD"
									>
										{lastWord}
									</CosmosText>
									<ArrowRightCircleFilledIcon className={cx(featureCardIcon, css({ marginInlineStart: "-24px" }))} />
								</>
							)}
						</CosmosCard.Link>
					</CosmosCard.ThumbnailOverlay>
				) : null}
			</CosmosCard.Thumbnail>
		</CosmosCard>
	);
}
